<script setup>
import { useI18n } from 'vue-i18n';
import { ref, watch, nextTick } from 'vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';

const props = defineProps({
  messages: {
    type: Array,
    required: true,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const messageContainer = ref(null);
const audioRefs = ref({});
const autoPlayedMessages = ref(new Set());

const { t } = useI18n();
const { formatMessage } = useMessageFormatter();

const isUserMessage = sender => sender === 'user';

const getMessageAlignment = sender =>
  isUserMessage(sender) ? 'justify-end' : 'justify-start';

const getMessageDirection = sender =>
  isUserMessage(sender) ? 'flex-row-reverse' : 'flex-row';

const getAvatarName = sender =>
  isUserMessage(sender)
    ? t('CAPTAIN.PLAYGROUND.USER')
    : t('CAPTAIN.PLAYGROUND.ASSISTANT');

const getMessageStyle = sender =>
  isUserMessage(sender)
    ? 'bg-n-strong text-n-white'
    : 'bg-n-solid-iris text-n-slate-12';

const scrollToBottom = async () => {
  await nextTick();
  if (messageContainer.value) {
    messageContainer.value.scrollTop = messageContainer.value.scrollHeight;
  }
};

const setAudioRef = (el, index) => {
  if (el) {
    audioRefs.value[index] = el;
  }
};

// Auto-play audio only for NEW assistant messages with audioUrl
watch(
  () => props.messages,
  async (newMessages, oldMessages) => {
    await scrollToBottom();
    
    // Only proceed if there are actually new messages
    if (!oldMessages || newMessages.length <= oldMessages.length) {
      return;
    }
    
    // Find the newest assistant message with audioUrl that wasn't in the old messages
    const oldMessageCount = oldMessages.length;
    const newAssistantMessages = newMessages.slice(oldMessageCount).filter(
      msg => !isUserMessage(msg.sender) && msg.audioUrl
    );
    
    // Only auto-play the most recent new assistant message with audio
    if (newAssistantMessages.length > 0) {
      const newestAssistantMessage = newAssistantMessages[newAssistantMessages.length - 1];
      const messageIndex = newMessages.indexOf(newestAssistantMessage);
      
      // Create a unique key for this message (using index and timestamp)
      const messageKey = `${messageIndex}-${newestAssistantMessage.timestamp || Date.now()}`;
      
      // Only auto-play if we haven't already played this message
      if (!autoPlayedMessages.value.has(messageKey)) {
        autoPlayedMessages.value.add(messageKey);
        const audioElement = audioRefs.value[messageIndex];
        
        if (audioElement) {
          // Wait a bit for the audio element to be ready
          await nextTick();
          try {
            audioElement.play().catch(error => {
              console.log('[CAPTAIN][Playground][DEBUG] Auto-play blocked:', error);
              // Auto-play was blocked by browser, user will need to click play
              // Remove from set so it can be retried if needed
              autoPlayedMessages.value.delete(messageKey);
            });
          } catch (error) {
            console.log('[CAPTAIN][Playground][DEBUG] Auto-play error:', error);
            autoPlayedMessages.value.delete(messageKey);
          }
        }
      }
    }
  },
  { deep: true }
);

watch(() => props.messages.length, scrollToBottom);
</script>

<template>
  <div ref="messageContainer" class="flex-1 overflow-y-auto mb-4 space-y-2">
    <div
      v-for="(message, index) in messages"
      :key="index"
      class="flex"
      :class="getMessageAlignment(message.sender)"
    >
      <div
        class="flex items-start gap-1.5"
        :class="getMessageDirection(message.sender)"
      >
        <Avatar :name="getAvatarName(message.sender)" rounded-full :size="24" />
        <div
          class="max-w-[80%] rounded-lg p-3 text-sm"
          :class="getMessageStyle(message.sender)"
        >
          <div class="break-words" v-html="formatMessage(message.content)" />
          <div v-if="message.audioUrl && !isUserMessage(message.sender)" class="mt-2">
            <audio
              :ref="el => setAudioRef(el, index)"
              controls
              class="w-full h-10"
              preload="auto"
            >
              <source :src="message.audioUrl" type="audio/mpeg" />
              Your browser does not support the audio element.
            </audio>
          </div>
        </div>
      </div>
    </div>
    <div v-if="isLoading" class="flex justify-start">
      <div class="flex items-start gap-1.5">
        <Avatar :name="getAvatarName('assistant')" rounded-full :size="24" />
        <div
          class="max-w-sm rounded-lg p-3 text-sm bg-n-solid-iris text-n-slate-12"
        >
          <div class="flex gap-1">
            <div class="w-2 h-2 rounded-full bg-n-iris-10 animate-bounce" />
            <div
              class="w-2 h-2 rounded-full bg-n-iris-10 animate-bounce [animation-delay:0.2s]"
            />
            <div
              class="w-2 h-2 rounded-full bg-n-iris-10 animate-bounce [animation-delay:0.4s]"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
